<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import InvoiceItems from '@/components/FareOptimizer/InvoiceItems.vue'
import InvoiceReader from '@/components/Globals/InvoiceReader.vue'
import OpportunityCalc from '@/components/FareOptimizer/OpportunityCalc.vue'
import PeakWasteAnalysis from '@/components/FareOptimizer/PeakWasteAnalysis.vue'
import IcLoader from '@/components/Icons/IcLoader.vue'
import { FareItems } from '@/interfaces/entities/fareItems'
import ActionSuggetion from '@/components/FareOptimizer/ActionSuggetion.vue'
import OpportunityEditor from '@/components/FareOptimizer/OpportunityEditor.vue'
import InvoiceErrorDialog from '@/components/FareOptimizer/Dialog/InvoiceErrorDialog.vue'
import SaveInvoiceDialog from '@/components/FareOptimizer/Dialog/SaveInvoiceDialog.vue'
import HistoricalDataTable from '@/components/FareOptimizer/HistoricalDataTable.vue'
import InvoiceHistoryTab from '@/components/FareOptimizer/History/InvoiceHistoryTab.vue'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { useI18n } from '@/composable/useI18n'

type FareTabKey = 'optimizer' | 'opportunities' | 'history'

const { t } = useI18n()

const fareData = ref<FareItems | null>(null)
const activeTab = ref<FareTabKey>('optimizer')
const isSaveDialogOpen = ref(false)

const handleFareDataLoaded = (data: FareItems) => {
  fareData.value = data
}

const store = useFareOptimizerStore()
const { cleanInvoiceAlerts, generateReport, fetchConsumerUnits } = store
const {
  canGenerateReport,
  isGeneratingReport,
  canOpenSaveDialog,
  needsConsumerUnitOnboarding,
  consumerUnits,
} = storeToRefs(store)

const isHistoryDisabled = computed(() => consumerUnits.value.length === 0)

function openHistoryTab() {
  if (isHistoryDisabled.value) return
  activeTab.value = 'history'
}

watch(isHistoryDisabled, (disabled) => {
  if (disabled && activeTab.value === 'history') {
    activeTab.value = 'optimizer'
  }
})

onMounted(async () => {
  cleanInvoiceAlerts()
  await fetchConsumerUnits()
})
</script>

<template>
  <DefaultLayout>
    <div class="flex min-w-0 w-full max-w-300 flex-col p-6 lg:px-8">
      <div class="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div class="mb-4 flex max-w-lg flex-col sm:mb-0 sm:mr-4 sm:max-w-none">
          <div class="flex flex-wrap items-center gap-4 pb-4">
            <h1 class="text-title-md2 font-semibold text-black">
              {{ t('contract_optimizer.page.title') }}
            </h1>
          </div>
          <p class="max-w-prose text-left text-sm font-medium leading-relaxed text-body">
            {{ t('contract_optimizer.page.subtitle') }}
          </p>
        </div>
        <div v-if="activeTab !== 'history'" class="flex shrink-0 flex-wrap items-center gap-2">
          <button
            class="inline-flex items-center gap-2 border border-primary px-4 py-2 text-sm font-medium text-primary transition hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canOpenSaveDialog"
            @click="isSaveDialogOpen = true">
            {{ t('contract_optimizer.invoice.save') }}
          </button>
          <button
            class="inline-flex items-center gap-2 bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!canGenerateReport || isGeneratingReport"
            @click="generateReport">
            <IcLoader v-if="isGeneratingReport" class="h-4 w-4 animate-spin" />
            {{ isGeneratingReport ? t('contract_optimizer.report.generating') : t('contract_optimizer.report.generate') }}
          </button>
        </div>
      </div>

      <div class="mb-6 flex gap-1 border-b border-stroke">
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition"
          :class="activeTab === 'optimizer' ? 'border-b-2 border-primary text-primary' : 'text-slate-500'"
          @click="activeTab = 'optimizer'">
          {{ t('contract_optimizer.invoice_history.optimizer_tab') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition"
          :class="activeTab === 'opportunities' ? 'border-b-2 border-primary text-primary' : 'text-slate-500'"
          @click="activeTab = 'opportunities'">
          {{ t('contract_optimizer.opportunities.tab') }}
        </button>
        <button
          type="button"
          class="px-4 py-2 text-sm font-medium transition"
          :class="
            isHistoryDisabled
              ? 'cursor-not-allowed text-slate-300'
              : activeTab === 'history'
                ? 'border-b-2 border-primary text-primary'
                : 'text-slate-500'
          "
          :disabled="isHistoryDisabled"
          :title="isHistoryDisabled ? t('contract_optimizer.invoice_history.disabled_tooltip') : undefined"
          @click="openHistoryTab">
          {{ t('contract_optimizer.invoice_history.tab') }}
        </button>
      </div>

      <div v-if="activeTab === 'optimizer'" class="flex flex-col gap-6">
        <InvoiceErrorDialog />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-stretch">
          <div class="lg:col-span-9">
            <OpportunityCalc class="h-full w-full overflow-x-auto" />
          </div>
          <div class="lg:col-span-3">
            <InvoiceReader
              :show-first-invoice-hint="needsConsumerUnitOnboarding"
              class="h-full w-full"
              @fareDataLoaded="handleFareDataLoaded" />
          </div>
        </div>

        <PeakWasteAnalysis />

        <ActionSuggetion />

        <InvoiceItems :fareData="fareData" class="w-full overflow-x-auto" />
        <HistoricalDataTable class="w-full overflow-x-auto" />
      </div>

      <div v-else-if="activeTab === 'opportunities'">
        <InvoiceErrorDialog />
        <div class="flex flex-col gap-6">
          <OpportunityCalc class="w-full overflow-x-auto" />
          <OpportunityEditor />
        </div>
      </div>

      <div v-else>
        <InvoiceHistoryTab class="w-full overflow-x-auto" />
      </div>

      <SaveInvoiceDialog :is-open="isSaveDialogOpen" @close="isSaveDialogOpen = false" />
    </div>
  </DefaultLayout>
</template>
